/*
 * Chapter gallery (blocks/seatfit-media-chapters). Behaviour cloned from the competitor's media carousel:
 * - one swipeable stage; dots show the slides of the current chapter; chapter thumbnails jump to a chapter;
 * - the active slide's video plays muted while the gallery is on screen, and a video that ends moves on to the next slide
 *   (looping videos stay); videos load only when their slide becomes active, and a video that leaves the stage before
 *   it is fully downloaded stops downloading (a paused video kept buffering and took the bandwidth of the next one);
 * - posters (data-sfc-poster, 1080 px) are set only on the slide on show and its two neighbours;
 * - photo slides follow the selected Color option (photos are matched by ALT text); a shopper's own Color or Style
 *   pick brings the first photo of that colour on stage, and the first photo chapter's thumbnail shows that colour.
 *   Picks made by script (vehicle steps, a ?color= link) leave the stage where it is, so the page still opens on video;
 * - Style (Classic / Diamond ...): a slide with data-style shows only while html[data-sf-style] names it, the value the
 *   <head> CSS hides the same slides by (snippets/seatfit-style-state.liquid; moved by seatfit-style-state.js). */
(function () {
  'use strict';
  if (customElements.get('seatfit-media-chapters')) return;

  class SeatfitMediaChapters extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try { this.init(); } catch (e) { console.warn('[media chapters] init failed', e); }
    }

    init() {
      this.track = this.querySelector('[data-sfc-track]');
      this.all = Array.from(this.querySelectorAll('[data-sfc-slide]'));
      if (!this.track || !this.all.length) return;
      this.dots = this.querySelector('[data-sfc-dots]');
      this.row = this.querySelector('[data-sfc-chapters]');
      this.controls = this.querySelector('[data-sfc-controls]');
      this.active = -1; this.inView = false; this.unmuted = false; this.userPaused = false;
      // Style on show, '' = product without a Style option (this.look: an element's .style is its inline CSS).
      this.look = this.dataset.stylePosition ? document.documentElement.getAttribute('data-sf-style') || this.dataset.style || '' : '';
      this.color = this.dataset.color || '';
      this.refresh(true);
      this.bind();
      this.all.forEach((s) => { const v = s.querySelector('video'); if (v) v.addEventListener('ended', () => this.onEnded(v)); });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((en) => { this.inView = en[0].isIntersecting; this.syncPlayback(); }, { threshold: 0.3 }).observe(this);
      } else { this.inView = true; }
      this.setActive(0);
    }

    bind() {
      let raf = 0;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          if (!this.lock) this.setActive(Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth)));
        });
      }, { passive: true });
      this.on('[data-sfc-prev]', () => this.goTo(this.active - 1));
      this.on('[data-sfc-next]', () => this.goTo(this.active + 1));
      this.on('[data-sfc-chap-prev]', () => this.row.scrollBy({ left: -this.row.clientWidth * 0.8, behavior: 'smooth' }));
      this.on('[data-sfc-chap-next]', () => this.row.scrollBy({ left: this.row.clientWidth * 0.8, behavior: 'smooth' }));
      this.on('[data-sfc-mute]', () => { this.unmuted = !this.unmuted; this.syncPlayback(); });
      this.on('[data-sfc-pause]', () => { this.userPaused = !this.userPaused; this.syncPlayback(); });
      this.row.addEventListener('scroll', () => this.updateRowArrows(), { passive: true });
      this.track.addEventListener('click', (e) => {
        const more = e.target.closest('[data-sfc-more]');
        if (more) more.closest('[data-sfc-caption]').classList.add('is-open');
      });
      // A new width (phone rotation, window resize) keeps the same slide instead of letting scroll-snap pick one.
      window.addEventListener('resize', () => {
        this.lock = true;
        this.goTo(this.active, 'instant');
        setTimeout(() => { this.lock = false; }, 150);
      });
      // Color follows the variant picker: radio change, plus the theme's variant event for other ways of switching.
      // Only a trusted event is the shopper's own click: the vehicle steps and links pick by script (untrusted).
      const S = window.SeatFitStyle;
      const styleName = S && S.option;
      document.addEventListener('change', (e) => {
        const t = e.target;
        if (!t) return;
        if (t.name === 'Color') this.follow(t.value, null);
        if (e.isTrusted && (t.name === 'Color' || (styleName && t.name === styleName))) {
          const box = t.closest ? t.closest('variant-selects') : null;
          setTimeout(() => this.showColorPhoto(box), 0);
        }
      });
      const pos = parseInt(this.dataset.colorPosition, 10);
      if (pos && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (ev) => {
          const v = ev && ev.data && ev.data.variant;
          if (v && v.options) this.follow(v.options[pos - 1], null);
        });
      }
      // Style: only the moves of html[data-sf-style] (no seatfit-style-state.js = the style never changes, as the CSS).
      if (this.look && S && S.watch) S.watch((style) => { if (this.isConnected) this.follow(null, style); });
    }

    on(sel, fn) { const el = this.querySelector(sel); if (el) el.addEventListener('click', fn); }

    // Runs once every listener of the pick is done: the Style watcher has moved this.look, and a Style switch has
    // ticked (without an event) the colour the new style sells. That checked colour's first photo goes on stage.
    showColorPhoto(box) {
      if (!this.isConnected) return;
      const r = box ? Array.from(box.querySelectorAll('input[type="radio"]')).find((i) => i.name === 'Color' && i.checked) : null;
      if (r) this.follow(r.value, null);
      const i = this.slides.findIndex((s) => s.dataset.kind === 'photo');
      if (i < 0 || i === this.active) return;
      this.goTo(i, 'instant'); // instant: a smooth scroll would start every video slide it passes
      this.setActive(i);
    }

    // null = unchanged. A new style keeps the shopper on the same chapter when it has slides.
    follow(color, style) {
      const newStyle = style != null && style !== this.look;
      const newColor = color != null && color !== this.color;
      if (!newStyle && !newColor) return;
      if (newStyle) this.look = style;
      if (newColor) this.color = color;
      this.refresh(false, newStyle);
    }

    refresh(initial, keepChapter) {
      const current = this.slides ? this.slides[this.active] : null;
      this.all.forEach((s) => {
        let show = !this.look || !s.dataset.style || s.dataset.style === this.look;
        if (show && s.dataset.kind === 'photo') {
          const photos = Array.from(s.querySelectorAll('.sfc__photo'));
          let hit = photos.find((p) => p.dataset.color === this.color) || null;
          if (!hit && !this.color && photos.length) hit = photos[0];
          photos.forEach((p) => p.classList.toggle('is-active', p === hit));
          show = !!hit;
        }
        s.classList.toggle('is-empty', !show);
      });
      this.slides = this.all.filter((s) => !s.classList.contains('is-empty'));
      this.buildChapters();
      if (initial) return;
      let keep = current ? this.slides.indexOf(current) : -1;
      if (keep < 0 && keepChapter && current) keep = this.slides.findIndex((s) => s.dataset.chapter === current.dataset.chapter);
      keep = Math.max(0, keep);
      this.active = -1;
      this.goTo(keep, 'instant');
      this.setActive(keep);
    }

    buildChapters() {
      this.chapters = [];
      const firstPhoto = this.slides.findIndex((s) => s.dataset.kind === 'photo');
      this.slides.forEach((s, i) => {
        const name = s.dataset.chapter;
        let ch = this.chapters[this.chapters.length - 1];
        if (!ch || ch.name !== name) { ch = { name, start: i, count: 0, thumb: '', play: s.dataset.thumbVideo === 'true' }; this.chapters.push(ch); }
        ch.count += 1;
        if (i === firstPhoto) {
          // The colour chapter's thumbnail is the selected colour, cut like the other thumbnails (256 square).
          const img = s.querySelector('.sfc__photo.is-active img');
          const src = img ? img.getAttribute('src') || '' : '';
          if (src) ch.thumb = src.replace(/([?&])width=\d+/, '$1width=256&height=256&crop=center');
        }
        if (!ch.thumb && s.dataset.thumb) ch.thumb = s.dataset.thumb;
        if (!ch.thumb) {
          const img = s.querySelector('.sfc__photo.is-active img, img.sfc__media');
          if (img) ch.thumb = img.getAttribute('src');
        }
        s.dataset.chapterIndex = this.chapters.length - 1;
      });
      this.row.innerHTML = '';
      this.chapters.forEach((ch, k) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'sfc__chapter'; b.setAttribute('role', 'tab');
        const t = document.createElement('span');
        t.className = 'sfc__chapter-thumb' + (ch.play ? ' has-play' : '');
        if (ch.thumb) t.style.backgroundImage = 'url("' + ch.thumb + '")';
        const l = document.createElement('span');
        l.className = 'sfc__chapter-label'; l.textContent = ch.name;
        b.append(t, l);
        // Straight to the chapter: a smooth scroll made every video slide it passed active for a frame, and each one
        // started downloading (a jump across the gallery opened ~13 videos at once; on 4G the target never started).
        b.addEventListener('click', () => { this.goTo(ch.start, 'instant'); this.setActive(ch.start); });
        ch.button = b;
        this.row.appendChild(b);
      });
      this.updateRowArrows();
    }

    goTo(i, behavior) {
      if (!this.slides.length) return;
      const idx = Math.max(0, Math.min(this.slides.length - 1, i));
      const left = idx * this.track.clientWidth;
      if (behavior === 'instant') {
        this.track.style.scrollBehavior = 'auto';  // CSS smooth would otherwise animate an "instant" jump
        this.track.scrollLeft = left;
        this.track.style.scrollBehavior = '';
      } else {
        this.track.scrollTo({ left, behavior: behavior || 'smooth' });
      }
    }

    setActive(i) {
      const idx = Math.max(0, Math.min(this.slides.length - 1, i));
      if (idx === this.active || !this.slides[idx]) return;
      this.active = idx;
      this.userPaused = false;
      const slide = this.slides[idx];
      for (let k = idx - 1; k <= idx + 1; k += 1) {
        const v = this.slides[k] ? this.slides[k].querySelector('video[data-sfc-poster]') : null;
        if (v) { v.setAttribute('poster', v.getAttribute('data-sfc-poster')); v.removeAttribute('data-sfc-poster'); }
      }
      const ch = this.chapters[+slide.dataset.chapterIndex];
      this.dots.innerHTML = '';
      for (let k = 0; k < ch.count; k += 1) {
        const d = document.createElement('span');
        d.className = 'sfc__dot' + (ch.start + k === idx ? ' is-active' : '');
        this.dots.appendChild(d);
      }
      this.chapters.forEach((c) => { c.button.classList.toggle('is-active', c === ch); c.button.setAttribute('aria-selected', c === ch); });
      const b = ch.button;
      const left = b.offsetLeft - (this.row.clientWidth - b.offsetWidth) / 2;
      this.row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      this.querySelector('[data-sfc-prev]').disabled = idx === 0;
      this.querySelector('[data-sfc-next]').disabled = idx === this.slides.length - 1;
      this.syncPlayback();
    }

    syncPlayback() {
      const slide = this.slides[this.active];
      const video = slide ? slide.querySelector('video') : null;
      this.controls.hidden = !video;
      this.classList.toggle('is-unmuted', this.unmuted);
      this.classList.toggle('is-paused', this.userPaused);
      this.all.forEach((s) => {
        const v = s.querySelector('video');
        if (!v || v === video) return;
        if (!v.paused) v.pause();
        this.release(v);
      });
      if (!video) return;
      if (!this.inView || this.userPaused) { video.pause(); return; }
      this.ensureSource(video);
      video.muted = !this.unmuted;
      const p = video.play();
      // A chapter jump scrolls past other video slides whose play() pause() cuts short (AbortError): not a shopper
      // pause, and neither is a late refusal from a slide that is no longer the active one.
      if (p && p.catch) p.catch((err) => {
        if ((err && err.name === 'AbortError') || this.slides[this.active] !== slide) return;
        this.userPaused = true; this.classList.add('is-paused');
      });
    }

    ensureSource(video) {
      const pending = video.querySelectorAll('source[data-src]');
      if (!pending.length) return;
      pending.forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
      video.load();
    }

    // Stop a video that is off stage from downloading: its sources go back to data-src (ensureSource restores them and
    // it starts again from the top). A fully downloaded video keeps its data, so coming back to it costs nothing.
    release(video) {
      const live = video.querySelectorAll('source[src]');
      if (!live.length) return;
      const b = video.buffered;
      if (video.duration && b.length && b.end(b.length - 1) >= video.duration - 0.5) return;
      live.forEach((s) => { s.dataset.src = s.getAttribute('src'); s.removeAttribute('src'); });
      video.load();
    }

    onEnded(video) {
      const slide = this.slides[this.active];
      if (!slide || slide.querySelector('video') !== video || video.loop) return;
      if (this.active < this.slides.length - 1) this.goTo(this.active + 1);
    }

    updateRowArrows() {
      const prev = this.querySelector('[data-sfc-chap-prev]');
      const next = this.querySelector('[data-sfc-chap-next]');
      if (prev) prev.disabled = this.row.scrollLeft <= 2;
      if (next) next.disabled = this.row.scrollLeft + this.row.clientWidth >= this.row.scrollWidth - 2;
    }
  }

  customElements.define('seatfit-media-chapters', SeatfitMediaChapters);
})();
